<!-- App: contains map and its filters -->
<template>
  <TooltipProvider>
    <div class="map-app">
      <div class="site-header">
        <div class="site-title"><h3>HVIP Voucher Funding Map</h3></div>
      </div>
      <!-- <div class="copy-block"></div> -->
      <main class="app-main">
        <div v-if="isPageLoading" class="page-loading-overlay">
          <div class="page-loading-card">
            <i class="fa fa-spinner fa-spin"></i>
            <span>Loading vouchers...</span>
          </div>
        </div>

        <!-- Mobile floating action button to toggle filter view -->
        <button
          type="button"
          class="filters-fab"
          @click="filtersOpen = true"
          aria-controls="filters-drawer"
          :aria-expanded="filtersOpen"
          aria-label="Open filters"
        >
          <i class="fa fa-sliders" aria-hidden="true"></i>
        </button>

        <!-- Overlay + drawer (mobile) -->
        <Transition name="filters" appear>
          <div 
            v-if="isMobile" 
            v-show="filtersOpen"
            class="filters-overlay open" 
            @click.self="filtersOpen = false"
          >
            <aside
              id="filters-drawer"
              class="filters-drawer"
              role="dialog"
              aria-modal="true"
              @keydown.esc="filtersOpen = false"
              @click.stop
              tabIndex="-1"
            >
              <div class="filters-drawer-header">
                <button type="button" class="filters-close" @click="filtersOpen = false" aria-label="Close filters">
                  <i class="fa fa-xmark" aria-hidden="true"></i>
                </button>
              </div>

              <FiltersPanel
                :options="filterOptions"
                :program="activeProgram"
                v-model="filters"
                @reset="resetFilters"
                @geography-change="isMapUpdating = true" 
              />

            </aside>
          </div>
        </Transition>

        <section class="layout">

          <div class="panel panel-map" data-area="map">
            <KpiCards :vouchers="filteredVouchers" :program="activeProgram" />
            <div class="viz-toggle-container">
              <label class="form-label mb-0" style="line-height: 1;">Visualization Type</label>
              <div class="viz-toggle" role="group" aria-label="Visualization type">
                <button type="button" :class="{ active: activeViz === 'map' }" @click="activeViz = 'map'">
                  Voucher Funding Map
                </button>
                <button type="button" :class="{ active: activeViz === 'chart' }" @click="activeViz = 'chart'">
                  Vouchers Over Time
                </button>
              </div>
            </div>

            <div class="map-wrapper">
              <div v-show="isMapUpdating && activeViz === 'map'" class="map-loading-overlay">
                <i class="fa fa-spinner fa-spin"></i>
                <span>Updating map...</span>
              </div>
              <MapDashboard
                v-if="activeViz === 'map'"
                ref="mapRef"
                :geojson="featureCollection"
                :vouchers="filteredVouchers"
                :geography-type="filters.geographyType"
                :program="activeProgram"
                :feature-name-prop="'label'"
                :fit-bounds-on-load="true"
                @render-complete="isMapUpdating = false"
              />
              <VoucherCountByYearChart
                v-if="activeViz === 'chart'"
                :vouchers="filteredVouchers"
                :program="activeProgram"
              />
            </div>

            <!-- <DrivetrainLegend :items="drivetrainLegendItems" /> -->
            <div class="reporting-date">
              <small>Data current as of {{ reportingDate }}</small>
            </div>
            <div class="download-container">
              <DownloadMenu 
                :options="csvOptions" 
                :disabled="!reportingDateRaw"
                @select="(mode) => downloadVoucherCsv(mode, reportingDateRaw)" />
            </div>
          </div>
          <aside 
            v-if="!isMobile"
            id="filters-panel" 
            class="panel panel-filters desktop-filters" 
            data-area="filters" 
            :class="{ 'is-closed': !filtersOpen }">
           <FiltersPanel
              :options="filterOptions"
              v-model="filters"
              @reset="resetFilters"
              @geography-change="isMapUpdating = true" 
            />
            <DrivetrainBreakdownChart 
                :vouchers="filteredVouchers" 
              />
            <DrivetrainLegend :items="drivetrainLegendItems" />
          </aside>
        </section>
      </main>
    </div>
  </TooltipProvider>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, onBeforeUnmount } from 'vue'

import TooltipProvider from '@/components/TooltipProvider.vue'
import FiltersPanel from '@/components/FiltersPanel.vue'
import MapDashboard from '@/components/MapDashboard.vue'
import KpiCards from '@/components/KpiCards.vue' 
import VoucherCountByYearChart from '@/components/VoucherCountByYearChart.vue'
import DrivetrainBreakdownChart from '@/components/DrivetrainBreakdownChart.vue'
import DrivetrainLegend from '@/components/DrivetrainLegend.vue'
import DownloadMenu from '@/components/downloadMenu.vue'

import { useVoucherData } from '@/composables/useVoucherData'
import { useGeoLayers} from '@/composables/useGeoLayers'
import { getDiscreteChartColors } from '@/utils/chartUtils'

import { compareDrivetrain } from '@/constants/fieldOrders'


// ------------------------------------------------
// DATA SOURCES
// ------------------------------------------------
const {
  vouchers: hvipVouchers,
  loading: hvipLoading,
  error: hvipError,
  load: loadHvip,
} = useVoucherData("hvip")

const { loadLayer } = useGeoLayers()

// ------------------------------------------------
// UI STATE
// ------------------------------------------------
const hasInitialized = ref(false)
const isMobile = ref(false)
const filtersOpen = ref(true)
const showDownloadMenu = ref(true)
const activeViz = ref("map") // "map" or "chart"
const isMapUpdating = ref(true) // show spinner on map until first render completes
const activeGeoLayer = ref(null)

const airDistrictAliases = { 
  "Sacramento Metropolitan": "Sacramento Metro",
  "SCAQMD": "South Coast",
  "Yolo/Solano": "Yolo-Solano"
}

const drivetrainAliases = {
  // "ZEV-BEV": "Battery-electric",
  // "ZEV-FCV": "F
  // uel cell",
  "HV": "Hybrid"
}

const filters = ref({
  redemptionStatus: [],    
  geographyType: "county", 
  region: [],        
  drivetrain: [],             
  pubOrPriv: [],
  purchaser: [],
  manufacturer: [],
  dealership: [],
  gvwr: [],
  vocationalUse: [],
  infrastructure: [],
  voucherCategory: [],
  smallFleet: [],
  dac: [],
  drayageOperations: [],
})

const MULTI_KEYS = [
  "redemptionStatus",
  "region",
  "voucherRequestYear",
  "drivetrain",
  "pubOrPriv",
  "purchaser",
  "manufacturer",
  "dealership",
  "gvwr",
  "vocationalUse",
  "infrastructure",
  "voucherCategory",
  "smallFleet",
  "dac",
  "drayageOperations",
]

const LAYER_BY_GEOGRAPHY_TYPE = {
  county: "counties",
  assemblyDistrict: "assembly_districts",
  senateDistrict: "senate_districts",
  airDistrict: "air_districts",
  censusTract: "census_tracts",
  zipCode: "zip_codes"
}

// ------------------------------------------------
// FUNCTIONS (helpers)
// ------------------------------------------------
function parseLocalDate(dateStr) {
  if (!dateStr) return null
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-indexed, constructs in local time
}

function normalizeHvipVoucher(v) {
  return {
    voucherId: v.voucher_id ?? "No data",
    redemptionStatus: v.redemption_status ?? "No data",
    fundingAmount: Number(v.amount ?? 0),
    drivetrain: v.drivetrain ?? "No data",
    voucherRequestYear: v.voucher_requested_year ?? "No data",
    voucherRequestDate: v.voucher_requested_date ?? "No data",
    manufacturer: v.manufacturer ?? "No data",
    vocationalUse: v.vocation ?? "No data", 
    purchaser: v.purchaser_company ?? "No data",
    dealership: v.dealership_company ?? "No data",
    gvwr: v.gross_vehicle_weight ?? "No data",
    pubOrPriv: v.pub_or_priv ?? "No data",
    dac: v.dac ?? "No data",
    infrastructure: v.infrastructure ?? "No data",
    smallFleet: v.small_fleet ?? "No data",
    voucherCategory: v.voucher_category ?? "No data",
    drayageOperations: v.drayage_operations ?? "No data",
    county: v.county ?? "No data",
    airDistrict: v.air_district ?? "No data",
    assemblyDistrict: v.assembly_district ?? "No data",
    senateDistrict: v.senate_district ?? "No data",
    censusTract: v.census_tract ?? "No data",
    zipCode: v.zip_code ?? "No data",
    geometry: v.geometry,
    reportingDate: parseLocalDate(v.date_imported),
    raw: v,
  }
}

function selectAll(options) {
  const next = { ...filters.value }
  next.geographyType = "county"
  for (const key of MULTI_KEYS) {
    next[key] = [...(options[key] ?? [])]
  }
  filters.value = next
}

function matchesMulti(value, selected) {
  if (!Array.isArray(selected) || selected.length === 0) return true
  return selected.includes(value)
}


const uniqSorted = (arr) => {
  const priority = {
    "Yes": 1,
    "No": 2,
    "Other": 97,
    "Unknown": 98,
    "No data": 99,
  }
  return [...new Set(arr)]
    .filter(v => v != null && v !== "")
    .sort((a, b) => {
      const pa = priority[a] ?? 50
      const pb = priority[b] ?? 50
      if (pa !== pb) return pa - pb // If priorities differ, use that
      return String(a).localeCompare(String(b)) // Otherwise alphabetical
    })
}

const uniqSortedGvwr = (arr) => {
  const priority = {
    "0–6,000 lbs": 1,
    "6,001–8,500 lbs": 2,
    "8,501–10,000 lbs": 3,
    "10,001–14,000 lbs": 4,
    "14,001–16,000 lbs": 5,
    "16,001–19,500 lbs": 6, 
    "19,501–26,000 lbs": 7, 
    "26,001–33,000 lbs": 8,
    "≥33,001 lbs": 9,
    "EVSE": 10,
    "No data": 11
  }
  return [...new Set(arr)]
    .filter(v => v != null && v !== "")
    .sort((a, b) => {
      const pa = priority[a] ?? 50
      const pb = priority[b] ?? 50
      if (pa !== pb) return pa - pb // If priorities differ, use that
      return String(a).localeCompare(String(b)) // Otherwise alphabetical
    })
}

function updateIsMobile() {
  isMobile.value = window.matchMedia("(max-width: 768px)").matches
}

async function resetFilters() {
  const opts = filterOptions.value
  for (const key of Object.keys(opts)) {
    filters.value[key] = [...(opts[key] ?? [])]
  }
  filters.value.geographyType = "county"
  await nextTick()
  filtersOpen.value = false
}

// Prevent background scroll when filters drawer is open (mobile)
watch(filtersOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : ""
})

// ------------------------------------------------
// COMPUTEDS
// ------------------------------------------------
const activeProgram = ref("hvip")

const vouchers = computed(() =>
  hvipVouchers.value
)

const drivetrains = computed(() =>
  [...new Set(
    (filteredVouchers.value ?? [])
      .map(v => v.drivetrain)
      .filter(Boolean)
  )].sort(compareDrivetrain)
)

const drivetrainLegendItems = computed(() => {
  const colors = getDiscreteChartColors()
  return drivetrains.value.map((type, index) => ({
    label: type,
    color: colors[index % colors.length],
  }))
})

const normalizedVouchers = computed(() => {
  const rows = vouchers.value ?? []
  return rows.map(normalizeHvipVoucher).map(v => {
    for (const key of Object.keys(v)) {
      if (v[key] === "No Data") v[key] = "No data"
    }
    if (v.airDistrict && airDistrictAliases[v.airDistrict]) {
      v.airDistrict = airDistrictAliases[v.airDistrict]
    }
    if (v.drivetrain && drivetrainAliases[v.drivetrain]) {
      v.drivetrain = drivetrainAliases[v.drivetrain]
    }
    return v
  })
})

const regionOptions = computed(() => {
  const rows = normalizedVouchers.value ?? []
  const geo = filters.value.geographyType
  return uniqSorted(rows.map(r => r[geo]))
})


const filterOptions = computed(() => {
  const rows = normalizedVouchers.value ?? []
  const geo = filters.value.geographyType
  return {
    redemptionStatus: uniqSorted(rows.map(r => r.redemptionStatus)),
    region: regionOptions.value,
    voucherRequestYear: uniqSorted(rows.map(r => r.voucherRequestYear)),
    drivetrain: uniqSorted(rows.map(r => r.drivetrain)).sort(compareDrivetrain),
    pubOrPriv: uniqSorted(rows.map(r => r.pubOrPriv)),
    gvwr: uniqSortedGvwr(rows.map(r => r.gvwr)),
    vocationalUse: uniqSorted(rows.map(r => r.vocationalUse)),
    purchaser: uniqSorted(rows.map(r => r.purchaser)),
    dealership: uniqSorted(rows.map(r => r.dealership)),
    manufacturer: uniqSorted(rows.map(r => r.manufacturer)),
    infrastructure: uniqSorted(rows.map(r => r.infrastructure)),
    voucherCategory: uniqSorted(rows.map(r => r.voucherCategory)),
    smallFleet: uniqSorted(rows.map(r => r.smallFleet)),
    dac: uniqSorted(rows.map(r => r.dac)),
    drayageOperations: uniqSorted(rows.map(r => r.drayageOperations)),
  }
})

const filteredVouchers = computed(() => {
  const rows = normalizedVouchers.value ?? []
  const f = filters.value
  return rows.filter(r => {
    if (!matchesMulti(r.redemptionStatus, f.redemptionStatus)) return false
    const regionVal = r[f.geographyType]
    if (!matchesMulti(regionVal, f.region)) return false
    if (!matchesMulti(r.voucherRequestYear, f.voucherRequestYear)) return false
    if (!matchesMulti(r.drivetrain, f.drivetrain)) return false
    if (!matchesMulti(r.pubOrPriv, f.pubOrPriv)) return false
    if (!matchesMulti(r.gvwr, f.gvwr)) return false
    if (!matchesMulti(r.vocationalUse, f.vocationalUse)) return false
    if (!matchesMulti(r.purchaser, f.purchaser)) return false
    if (!matchesMulti(r.manufacturer, f.manufacturer)) return false
    if (!matchesMulti(r.dealership, f.dealership)) return false
    if (!matchesMulti(r.infrastructure, f.infrastructure)) return false
    if (!matchesMulti(r.voucherCategory, f.voucherCategory)) return false
    if (!matchesMulti(r.smallFleet, f.smallFleet)) return false
    if (!matchesMulti(r.dac, f.dac)
  ) return false
    if (!matchesMulti(r.drayageOperations, f.drayageOperations)) return false
    return true
  })
})

const activeLayerName = computed(() =>
  LAYER_BY_GEOGRAPHY_TYPE[filters.value.geographyType] ?? "counties"
)

const featureCollection = computed(() => {
  const layer = activeGeoLayer.value
  if (!layer) return null
  if (layer.type === "FeatureCollection" && Array.isArray(layer.features)) return layer
  if (Array.isArray(layer)) {
    return {
      type: "FeatureCollection",
      features: layer,
    }
  }
  return null
})

const loading = computed(() => hvipLoading.value)
const error = computed(() => hvipError.value)
const isPageLoading = computed(() => hvipLoading.value || !hvipVouchers.value.length)


const reportingDateRaw = computed(() => {
  // since all vouchers have the same reporting date, we can just take the first one
  // console.log("reporting date (raw): ", normalizedVouchers.value?.[0]?.reportingDate ?? null)
  return normalizedVouchers.value?.[0]?.reportingDate ?? null
})

const reportingDate = computed(() => {
  // formatted version for UI
  return reportingDateRaw.value ? reportingDateRaw.value.toLocaleDateString() : "Unknown"
})




// ------------------------------------------------
// LIFECYCLE
// ------------------------------------------------
onMounted(() => {
  updateIsMobile()
  window.addEventListener("resize", updateIsMobile) // Cleanup listener on unmount
})

// Initialize: load vouchers and geo layers, then select all filters
onMounted(async () => {
  const vouchersPromise = loadHvip()  // fetch hvip vouchers
  const geoPromise = loadLayer("counties").then(() => { // load counties geo layer
    // after counties is done, load everything else in background
    for (const layerName of LAYERS.filter(l => l !== "counties")) {
      loadLayer(layerName)
    }
  })
  await Promise.all([vouchersPromise, geoPromise])
  await nextTick() // wait for next tick to ensure the DOM is updated before selecting all filters
  selectAll(filterOptions.value) // select all filters after vouchers are loaded
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateIsMobile)
})

onBeforeUnmount(() => {
  document.body.style.overflow = ""
})


// ------------------------------------------------
// WATCHERS
// ------------------------------------------------
watch(
  [filterOptions, normalizedVouchers, loading],
  ([opts, rows, isLoading]) => {
    if (hasInitialized.value) return
    if (isLoading) return
    if (!rows?.length) return
    const hasAnyOptions = Object.values(opts).some(
      arr => Array.isArray(arr) && arr.length > 0
    )
    if (!hasAnyOptions) return
    selectAll(opts)
    hasInitialized.value = true
  },
  { immediate: true, flush: "post" }
)

// set isMapUpdating true immediately when geography type changes
watch(
  () => filters.value.geographyType,
  () => {
    isMapUpdating.value = true
  }
)

let currentLoadId = 0
watch(activeLayerName, async (layerName, oldLayerName) => {
  const loadId = ++currentLoadId
  // console.log('1. activeLayerName changed:', oldLayerName, '->', layerName, 'loadId:', loadId)
  isMapUpdating.value = true
  // console.log('2. isMapUpdating set to true')
  if (oldLayerName !== undefined) {
    await nextTick()
    // console.log('3. after nextTick')
    await new Promise(resolve => requestAnimationFrame(resolve))
    // console.log('4. after rAF, resetting region')
    filters.value = {
      ...filters.value,
      region: [...regionOptions.value],
    }
  }
  // console.log('5. calling loadLayer for', layerName)
  const layer = await loadLayer(layerName)
  // console.log('6. loadLayer done for', layerName, 'loadId:', loadId, 'currentLoadId:', currentLoadId)
  if (loadId !== currentLoadId) {
    // console.log('7. stale load, discarding', layerName)
    return
  }
  // console.log('8. setting activeGeoLayer for', layerName)
  activeGeoLayer.value = layer
}, { immediate: true })




// function emptyMultiFilters() {
//   const next = { ...filters.value }
//   for (const key of MULTI_KEYS) {
//     next[key] = []
//   }
//   next.geographyType = "county"
//   filters.value = next
// }







const REDEEMED_STATUSES = new Set([
  "Completed Paid"
])

const UNREDEEMED_STATUSES = new Set([
  "Accepted Pending Confirmation",
  "Approved",
  "Redemption Approved", 
  "Redemption Processing",
])

function categorizeRedemptionStatus(status) {
  if (REDEEMED_STATUSES.has(status)) return "Redeemed"
  if (UNREDEEMED_STATUSES.has(status)) return "Unredeemed"
  return "No data"
}









// ------------------------------------------------
// FILTERS (keys match FiltersPanel.blocks keys)
// ------------------------------------------------



// ------------------------------------------------
// Tooltip text lookup
// ------------------------------------------------
// add later


// ------------------------------------------------
// FILTER OPTIONS (from vouchers; keys match FiltersPanel.blocks keys) 
// ------------------------------------------------




// ------------------------------------------------
// DEFAULT selections (select all once vouchers are loaded)
// ------------------------------------------------
const initializedByProgram = ref({
  core: false,
  sore: false,
})



// ------------------------------------------------
// FILTER vouchers
// ------------------------------------------------
const hasAny = (arr) => Array.isArray(arr) && arr.length > 0



// ------------------------------------------------
// GEO LAYERS
// ------------------------------------------------


const LAYERS = Object.values(LAYER_BY_GEOGRAPHY_TYPE) // all layer names


// DOWNLOAD CSV
// ------------------------------------------------
const csvOptions = [
  { value: 'full', label: 'Download Full Dataset', icon: 'fa fa-file-csv' },
  { value: 'filtered', label: 'Download Filtered Dataset', icon: 'fa fa-file-csv' },
]

function downloadVoucherCsv(mode = "full", currentReportingDate) {
  const rows = 
    mode === "filtered" 
      ? filteredVouchers.value 
      : normalizedVouchers.value

  if (!rows.length) return

  const columns = [
    { key: "voucherId", label: "Voucher ID" },
    { key: "redemptionStatus", label: "Redemption Status" },
    { key: "fundingAmount", label: "Funding Amount" },
    { key: "drivetrain", label: "Drivetrain" },
    { key: "voucherRequestDate", label: "Voucher Request Date" },
    { key: "manufacturer", label: "Manufacturer" },
    { key: "vocationalUse", label: "Vocational Use" },
    { key: "purchaser", label: "Purchaser Entity" },
    { key: "dealership", label: "Dealership" },
    { key: "gvwr", label: "GVWR" },
    { key: "pubOrPriv", label: "Purchaser Type" },
    { key: "dac", label: "Disadvantaged Community" },
    { key: "infrastructure", label: "Infrastructure" },
    { key: "smallFleet", label: "Small Fleet" },
    { key: "voucherCategory", label: "Voucher Category" },
    { key: "drayageOperations", label: "Drayage Operations" },
    { key: "county", label: "County" },
    { key: "airDistrict", label: "Air Quality District" },
    { key: "assemblyDistrict", label: "Assembly District" },
    { key: "senateDistrict", label: "Senate District" },
    { key: "censusTract", label: "Census Tract" },
    { key: "zipCode", label: "ZIP Code" },
    { key: "reportingDate", label: "Data Current As Of Date" },
]
  const csv = [
    columns.map(c => c.label).join(","),
    ...rows.map(row =>
      columns.map(col => {
        const value = row[col.key] ?? ""

        return `"${String(value)
          .replace(/"/g, '""')
          .replace(/\n/g, " ")}"`
      }).join(",")
    )
  ].join("\n")

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })

  const url = URL.createObjectURL(blob)

  const filenameDate =
    currentReportingDate instanceof Date && !isNaN(currentReportingDate)
      ? currentReportingDate.toISOString().slice(0, 10)
      : "unknown-date"
  
  const link = document.createElement("a")
  link.href = url
  link.download = `${activeProgram.value}_vouchers_${mode}_${filenameDate}.csv`
  link.click()

  URL.revokeObjectURL(url)
}



</script>